import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import { SECURITY_QUESTIONS } from "../lib/securityQuestions";
import { formatApiError } from "../lib/errors";

const Register = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    clinic_name: "",
    clinic_address: "",
    phone: "",
    secret_question: SECURITY_QUESTIONS[0],
    secret_answer: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register({
        ...form,
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
      });
      nav("/app/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2, "Registration failed"));
    } finally {
      setBusy(false);
    }
  };

  const field = (label, key, type = "text", required = false, placeholder = "") => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A5F] focus:border-transparent text-sm"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E6F7F5] via-white to-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-white rounded-lg shadow-xl border border-slate-100 p-8">
        <Link to="/" className="flex justify-center mb-6">
          <Logo size="lg" />
        </Link>
        <h1 className="text-2xl font-bold text-slate-800 text-center mb-2">Create Doctor Account</h1>
        <p className="text-center text-slate-500 text-sm mb-6">Get started in less than a minute</p>

        <form onSubmit={submit} className="space-y-4">
          {field("Full Name", "name", "text", true, "Dr. Anaya Sharma")}
          {field("Email", "email", "email", true, "doctor@clinic.com")}
          {field("Password", "password", "password", true, "Minimum 6 characters")}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field("Clinic Name", "clinic_name", "text", false, "Sunrise Dental")}
            {field("Phone", "phone", "tel", false, "+91 90000 00000")}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Clinic Address</label>
            <textarea
              rows={2}
              value={form.clinic_address}
              onChange={(e) => set("clinic_address", e.target.value)}
              placeholder="e.g. 69FF, Zirakpur, Mohali, Punjab"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A5F] focus:border-transparent text-sm"
            />
          </div>

          <div className="pt-2 border-t border-slate-100">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              Security question (for self password reset)
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Question</label>
                <select
                  value={form.secret_question}
                  onChange={(e) => set("secret_question", e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A5F] focus:border-transparent text-sm bg-white"
                >
                  {SECURITY_QUESTIONS.map((q) => <option key={q}>{q}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Answer <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.secret_answer}
                  onChange={(e) => set("secret_answer", e.target.value)}
                  placeholder="Case-insensitive. Save this somewhere safe."
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A5F] focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>

          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#14B8A6] hover:bg-[#0F8A7D] disabled:opacity-70 text-white font-semibold uppercase tracking-wide py-2.5 rounded-md flex items-center justify-center gap-2 transition-colors"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            Create Account
          </button>
        </form>

        <p className="text-center text-sm text-slate-600 mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-[#0F2A5F] font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
